import { FC, ReactNode, ChangeEvent, useState, useCallback, useEffect, useContext, createContext, useRef } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { Question, Trash } from '@phosphor-icons/react';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Input } from 'client/components/Input';
import { Typography } from 'client/components/Typography';
import { applyEffect } from 'client/Theme';

export interface DeleteDialogProps {
  open: boolean;
  targetID?: string;
  targetName?: string;
  targetType: string;
  action?: string;
  title?: string | null;
  description?: string | null;
  icon?: JSX.Element;
  buttonIcon?: JSX.Element;
  isPermanent?: boolean;
  onDelete: (targetID: string) => void;
  onClose: () => void;
}

export const DeleteDialog: FC<DeleteDialogProps> = ({
  open,
  targetID,
  targetName,
  targetType,
  action = 'delete',
  title,
  description,
  icon = <Trash />,
  buttonIcon = <Trash />,
  isPermanent = true,
  onDelete,
  onClose,
}) => {
  const [confirmation, setConfirmation] = useState('');
  const theme = useTheme();

  const handleConfirmationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setConfirmation(event.target.value), []);

  const handleDelete = useCallback(() => {
    setConfirmation((currentConfirmation) => {
      if (targetName === currentConfirmation || targetType === currentConfirmation) {
        onDelete(targetID || targetType);
      }
      return currentConfirmation;
    });
  }, [targetName, onDelete]);

  useEffect(() => {
    if (open) setConfirmation('');
  }, [open]);

  return (
    <Dialog
      open={open}
      title={title || 'Are you absolutely sure?'}
      description={description || `Confirm ${action} of ${targetName ? `"${targetName}"` : ''} ${targetType}`}
      icon={<icon.type color={theme.color.red[100]} />}
      iconOrnament={<Question color={theme.color.red[100]} />}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            primary
            icon={<buttonIcon.type />}
            autoFocus
            disabled={confirmation !== targetName && confirmation !== targetType}
            onClick={handleDelete}
            css={buttonStyle}
          >
            {action}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Typography variant="body1">
        You are about to {action} the {targetName ? `"${targetName}"` : ''} {targetType}.{' '}
        {isPermanent && (
          <>
            This action <b>cannot</b> be undone.{' '}
          </>
        )}
        Please make sure this is the intended action & target before proceeding.
      </Typography>
      <Banner type="warning" css={bannerStyle}>
        Type "{targetName || targetType}" in the text box below to confirm {action}.
      </Banner>
      <Input
        onClick={(e) => {
          if (e.metaKey || (e.shiftKey && e.altKey && e.ctrlKey)) setConfirmation(targetName || targetType);
        }}
        id="deleteConfirmationDialog.confimationInput"
        label="Confirmation"
        value={confirmation}
        onChange={handleConfirmationChange}
        css={inputStyle}
      />
    </Dialog>
  );
};

const DeleteConfirmContext = createContext<{
  targetID: string | null;
  confirmedDeletion: string | null;
  confirm: (props: Omit<DeleteDialogProps, 'open' | 'onDelete' | 'onClose'>) => void;
}>({
  targetID: null,
  confirmedDeletion: null,
  confirm: () => {},
});

export const DeleteConfirmProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [props, setProps] = useState<Omit<DeleteDialogProps, 'open' | 'onDelete' | 'onClose'> | null>(null);
  const [confirmedDeletion, setConfirmedDeletion] = useState<string | null>(null);

  const handleDelete = useCallback(
    (targetID: string) => {
      setConfirmedDeletion(targetID);
      setProps(null);
    },
    [props]
  );

  const handleCancel = useCallback(() => {
    setConfirmedDeletion(null);
    setProps(null);
  }, [props]);

  return (
    <DeleteConfirmContext.Provider value={{ targetID: props?.targetID || null, confirmedDeletion, confirm: setProps }}>
      {children}
      <DeleteDialog
        open={!!props}
        {...(props || { targetID: '', targetName: '', targetType: '' })}
        onDelete={handleDelete}
        onClose={handleCancel}
      />
    </DeleteConfirmContext.Provider>
  );
};

export const useConfirmDelete = () => {
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const [pendingPromise, setPendingPromise] = useState<((deleted: boolean) => void) | null>(null);

  const stateRef = useRef({
    pendingTarget,
    pendingPromise,
  });

  stateRef.current.pendingTarget = pendingTarget;
  stateRef.current.pendingPromise = pendingPromise;

  const { targetID, confirmedDeletion, confirm } = useContext(DeleteConfirmContext);

  useEffect(() => {
    const { pendingTarget, pendingPromise } = stateRef.current;

    if (pendingTarget === null) return;
    if (pendingTarget === confirmedDeletion && pendingPromise) {
      pendingPromise(true);
      setPendingPromise(null);
    }
  }, [confirmedDeletion]);

  useEffect(() => {
    const { pendingTarget, pendingPromise } = stateRef.current;

    if (pendingTarget === null) return;
    if (pendingTarget !== targetID && pendingPromise) {
      pendingPromise(false);
      setPendingPromise(null);
    }
  }, [targetID]);

  const confirmDelete = useCallback(
    (props: Omit<DeleteDialogProps, 'targetID' | 'open' | 'onDelete' | 'onClose'>) =>
      new Promise((resolve) => {
        const targetID = `${Date.now()}${Math.random()}`;
        confirm({ ...props, targetID });
        setPendingTarget(targetID);
        setPendingPromise(() => resolve);
      }),
    []
  );

  return confirmDelete;
};

const bannerStyle = css`
  margin-top: 1.5rem;
`;

const inputStyle = css`
  margin-top: 1rem;
`;

const buttonStyle = (theme: Theme) => css`
  background-color: ${theme.color.red[100]};
  text-transform: capitalize;

  &:hover {
    background-color: ${applyEffect(theme.color.red[100], 'hover')};
  }
`;
